import useFetch from '../../../hooks/useFetch';
import { ServiceEvents } from '../shared/interfaces';
import { useState } from 'react';
import { EndPoints } from '../EndPoints';
import {
  PackingOrderDataForm,
  PackingOrderFilters,
  PackingOrderOutputProductForm,
  PackingOrderInputProductForm
} from '../../models/PackingOrder';

const usePackingOrderService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();

  const [fetchingGetRecipeRequirements, setFetchingGetRecipeRequirements] = useState(false);
  const [fetchingGetPackingOrders, setFetchingGetPackingOrders] = useState(false);
  const [fetchingCreatePackingOrder, setFetchingCreatePackingOrder] = useState(false);
  const [fetchingStorePackingOrder, setFetchingStorePackingOrder] = useState(false);
  const [fetchingEditPackingOrder, setFetchingEditPackingOrder] = useState(false);
  const [fetchingUpdatePackingOrder, setFetchingUpdatePackingOrder] = useState(false);
  const [fetchingDeletePackingOrder, setFetchingDeletePackingOrder] = useState(false);
  const [fetchingActivePackingOrder, setFetchingActivePackingOrder] = useState(false);
  const [fetchingOutputProducts, setFetchingOutputProducts] = useState(false);
  const [fetchingInputProducts, setFetchingInputProducts] = useState(false);
  const [fetchingRequiredStock, setFetchingRequiredStock] = useState(false);
  const [fetchingEmpacarData, setFetchingEmpacarData] = useState(false);
  const [fetchingEmpacarExecute, setFetchingEmpacarExecute] = useState(false);
  const [fetchingProducedBatches, setFetchingProducedBatches] = useState(false);
  const [fetchingGetOrderIncidents, setFetchingGetOrderIncidents] = useState(false);
  const [fetchingStoreOrderIncident, setFetchingStoreOrderIncident] = useState(false);
  const [fetchingUpdateOrderIncident, setFetchingUpdateOrderIncident] = useState(false);
  const [fetchingDeleteOrderIncident, setFetchingDeleteOrderIncident] = useState(false);

  const getPackingOrders = (filters: PackingOrderFilters, events: ServiceEvents = {}) => {
    const queryString = Object.entries(filters)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => `${k}=${v}`)
      .join('&');
    doGet({
      ...events,
      url: `${EndPoints.PACKING_ORDERS.GET_PACKING_ORDERS}?${queryString}`,
      setFetching: setFetchingGetPackingOrders
    });
  };

  const createPackingOrder = (events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.PACKING_ORDERS.CREATE_PACKING_ORDER,
      setFetching: setFetchingCreatePackingOrder
    });

  const storePackingOrder = (data: PackingOrderDataForm, events: ServiceEvents = {}) =>
    doPost({
      ...events,
      url: EndPoints.PACKING_ORDERS.STORE_PACKING_ORDER,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      requestType: 'json',
      body: data,
      setFetching: setFetchingStorePackingOrder
    });

  const editPackingOrder = (id: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.PACKING_ORDERS.EDIT_PACKING_ORDER.replace(':id', id.toString()),
      setFetching: setFetchingEditPackingOrder
    });

  const updatePackingOrder = (id: number, data: PackingOrderDataForm, events: ServiceEvents = {}) =>
    doPut({
      ...events,
      url: EndPoints.PACKING_ORDERS.UPDATE_PACKING_ORDER.replace(':id', id.toString()),
      body: data,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      requestType: 'json',
      setFetching: setFetchingUpdatePackingOrder
    });

  const deletePackingOrder = (id: number, events: ServiceEvents = {}) =>
    doDelete({
      ...events,
      url: EndPoints.PACKING_ORDERS.DELETE_PACKING_ORDER.replace(':id', id.toString()),
      setFetching: setFetchingDeletePackingOrder
    });

  const activePackingOrder = (id: number, active: boolean, events: ServiceEvents = {}) =>
    doPost({
      ...events,
      url: EndPoints.PACKING_ORDERS.ACTIVE_PACKING_ORDER.replace(':id', id.toString()),
      body: { active },
      setFetching: setFetchingActivePackingOrder
    });

  const getOutputProducts = (packingOrderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.PACKING_ORDERS.GET_OUTPUT_PRODUCTS.replace(':packing_order_id', packingOrderId.toString()),
      setFetching: setFetchingOutputProducts
    });

  const storeOutputProduct = (data: PackingOrderOutputProductForm, events: ServiceEvents = {}) =>
    doPost({
      ...events,
      url: EndPoints.PACKING_ORDERS.STORE_OUTPUT_PRODUCT,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      requestType: 'json',
      body: data,
      setFetching: setFetchingOutputProducts
    });

  const updateOutputProduct = (id: number, data: Partial<PackingOrderOutputProductForm>, events: ServiceEvents = {}) =>
    doPut({
      ...events,
      url: EndPoints.PACKING_ORDERS.UPDATE_OUTPUT_PRODUCT.replace(':id', id.toString()),
      body: data,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      requestType: 'json',
      setFetching: setFetchingOutputProducts
    });

  const deleteOutputProduct = (id: number, events: ServiceEvents = {}) =>
    doDelete({
      ...events,
      url: EndPoints.PACKING_ORDERS.DELETE_OUTPUT_PRODUCT.replace(':id', id.toString()),
      setFetching: setFetchingOutputProducts
    });

  const getInputProducts = (packingOrderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.PACKING_ORDERS.GET_INPUT_PRODUCTS.replace(':packing_order_id', packingOrderId.toString()),
      setFetching: setFetchingInputProducts
    });

  const storeInputProduct = (data: PackingOrderInputProductForm, events: ServiceEvents = {}) =>
    doPost({
      ...events,
      url: EndPoints.PACKING_ORDERS.STORE_INPUT_PRODUCT,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      requestType: 'json',
      body: data,
      setFetching: setFetchingInputProducts
    });

  const updateInputProduct = (id: number, data: Partial<PackingOrderInputProductForm>, events: ServiceEvents = {}) =>
    doPut({
      ...events,
      url: EndPoints.PACKING_ORDERS.UPDATE_INPUT_PRODUCT.replace(':id', id.toString()),
      body: data,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      requestType: 'json',
      setFetching: setFetchingInputProducts
    });

  const deleteInputProduct = (id: number, events: ServiceEvents = {}) =>
    doDelete({
      ...events,
      url: EndPoints.PACKING_ORDERS.DELETE_INPUT_PRODUCT.replace(':id', id.toString()),
      setFetching: setFetchingInputProducts
    });

  const getRequiredStock = (packingOrderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.PACKING_ORDERS.GET_REQUIRED_STOCK.replace(':packing_order_id', packingOrderId.toString()),
      setFetching: setFetchingRequiredStock
    });

  const getRecipeRequirements = (outputProductId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.PACKING_ORDERS.GET_RECIPE_REQUIREMENTS.replace(':output_product_id', outputProductId.toString()),
      setFetching: setFetchingGetRecipeRequirements
    });

  const getEmpacarData = (packingOrderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.PACKING_ORDERS.GET_EMPACAR_DATA.replace(':packing_order_id', packingOrderId.toString()),
      setFetching: setFetchingEmpacarData
    });

  const executeEmpacar = (
    packingOrderId: number,
    body: any,
    events: ServiceEvents = {}
  ) =>
    doPost({
      ...events,
      url: EndPoints.PACKING_ORDERS.POST_EMPACAR.replace(':packing_order_id', packingOrderId.toString()),
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      requestType: 'json',
      body,
      setFetching: setFetchingEmpacarExecute
    });

  const getProducedBatches = (packingOrderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.PACKING_ORDERS.GET_PRODUCED_BATCHES.replace(':packing_order_id', packingOrderId.toString()),
      setFetching: setFetchingProducedBatches
    });

  // Métodos para incidentes
  const getOrderIncidents = (
    packingOrderId: number,
    filters: { page?: number; page_size?: number } = {},
    events: ServiceEvents = {}
  ) => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { 
      if (v != null && String(v) !== '') q.set(k, String(v)); 
    });
    const url = `${EndPoints.PACKING_ORDERS.GET_ORDER_INCIDENTS.replace(':id', packingOrderId.toString())}${q.toString() ? '?' + q.toString() : ''}`;
    doGet({
      ...events,
      url,
      setFetching: setFetchingGetOrderIncidents
    });
  };

  const storeOrderIncident = (
    packingOrderId: number,
    body: FormData | { date: string; time: string; reason_id: number; description: string; status: string; solution?: string; evidence?: File },
    events: ServiceEvents = {}
  ) => {
    doPost({
      ...events,
      url: EndPoints.PACKING_ORDERS.STORE_ORDER_INCIDENT.replace(':id', packingOrderId.toString()),
      body,
      setFetching: setFetchingStoreOrderIncident
    });
  };

  const updateOrderIncident = (
    packingOrderId: number,
    incidentId: number,
    body: FormData | { date: string; time: string; reason_id: number; description: string; status: string; solution?: string; evidence?: File; remove_evidence?: boolean },
    events: ServiceEvents = {}
  ) => {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    doPut({
      ...events,
      url: EndPoints.PACKING_ORDERS.UPDATE_ORDER_INCIDENT
        .replace(':incident_id', incidentId.toString()),
      body,
      requestType: isFormData ? 'multipart' : 'json',
      setFetching: setFetchingUpdateOrderIncident
    });
  };

  const deleteOrderIncident = (
    packingOrderId: number,
    incidentId: number,
    events: ServiceEvents = {}
  ) =>
    doDelete({
      ...events,
      url: EndPoints.PACKING_ORDERS.DELETE_ORDER_INCIDENT
        .replace(':incident_id', incidentId.toString()),
      setFetching: setFetchingDeleteOrderIncident
    });

  return {
    fetchingGetPackingOrders,
    fetchingCreatePackingOrder,
    fetchingStorePackingOrder,
    fetchingEditPackingOrder,
    fetchingUpdatePackingOrder,
    fetchingDeletePackingOrder,
    fetchingActivePackingOrder,
    getPackingOrders,
    createPackingOrder,
    storePackingOrder,
    editPackingOrder,
    updatePackingOrder,
    deletePackingOrder,
    activePackingOrder,
    fetchingOutputProducts,
    fetchingInputProducts,
    getOutputProducts,
    storeOutputProduct,
    updateOutputProduct,
    deleteOutputProduct,
    getInputProducts,
    storeInputProduct,
    updateInputProduct,
    deleteInputProduct,
    fetchingGetOrderIncidents,
    fetchingStoreOrderIncident,
    fetchingUpdateOrderIncident,
    fetchingDeleteOrderIncident,
    fetchingRequiredStock,
    getEmpacarData,
    executeEmpacar,
    getProducedBatches,
    fetchingEmpacarData,
    fetchingEmpacarExecute,
    fetchingProducedBatches,
    getOrderIncidents,
    storeOrderIncident,
    updateOrderIncident,
    deleteOrderIncident,
    getRequiredStock,
    getRecipeRequirements,
    fetchingGetRecipeRequirements,
  };
};

export default usePackingOrderService;
